import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Typography,
    Stack,
    Paper,
    Divider,
    Button,
    Radio,
    RadioGroup,
    FormControlLabel,
    CircularProgress,
    Alert,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useCheckoutForm, CheckoutProcessStatusType } from "./hooks/useCheckoutForm";
import {
    OrderFinishedInsufficientStockProduct,
    OrderProduct,
    OrderProductFields,
    OrderCalculationViewFields,
} from "../../types/purchaseOrder/Order";
import { useSnackbar } from "notistack";
import { AppConfigFields } from "../../types/appConfigEntities";
import { useCart } from "@ecommerce/store";
import { CartItem } from "@ecommerce/store";
import { LG } from "../../styles/liquidGlass";
import { BUTTON_STYLES } from "../../styles/buttonStyles";

type Resolution = { action: "remove" } | { action: "adjust"; quantity: number };

type Mode = "editing" | "review";

export const CheckoutStockConflictResolver: React.FC = () => {
    const { processStatus, onSubmitOrder, isLoadingOrder, recalculateOrder } = useCheckoutForm();
    const { cartItems, removeFromCart, updateQuantity, addToCart } = useCart();
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();

    const conflicts = processStatus.insufficientStockProducts ?? [];

    const [resolutions, setResolutions] = useState<Record<string, Resolution>>({});
    const [mode, setMode] = useState<Mode>("editing");
    const [isSaving, setIsSaving] = useState(false);

    // bloqueo de finalización por mínimo
    const [meetsMinimum, setMeetsMinimum] = useState(true);
    const [lastSubtotal, setLastSubtotal] = useState<number | null>(null);

    // snapshot para re-agregar items eliminados
    const [conflictSnapshot, setConflictSnapshot] = useState<Record<string, CartItem>>({});

    const makeKey = (p: OrderFinishedInsufficientStockProduct) =>
        `${p.idProducto}-${p.idProductoVariante}`;

    const keyFromCartItem = (i: CartItem) => `${i.idProduct}-${i.idProductVariant}`;

    const stockMinimoCompra = window.APP_CONFIG?.[AppConfigFields.MinimumPurchaseQuantity] ?? 1;

    const precioMinimo = window.APP_CONFIG?.[AppConfigFields.MinimumPurchaseAmount] ?? 0;

    const formatMoney = (n: number) =>
        `$${n.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    const canAdjustQuantity = (p: OrderFinishedInsufficientStockProduct) =>
        p.stockActual > 0 && p.stockActual >= stockMinimoCompra;

    const conflictSig = useMemo(() => conflicts.map(makeKey).sort().join("|"), [conflicts]);

    const allResolved = useMemo(
        () => conflicts.every((p) => !!resolutions[makeKey(p)]),
        [conflicts, resolutions],
    );

    useEffect(() => {
        setMode("editing");
        setResolutions({});
        setMeetsMinimum(true);
        setLastSubtotal(null);

        const snap: Record<string, CartItem> = {};
        conflicts.forEach((p) => {
            const key = makeKey(p);
            const existing = cartItems.find((i) => keyFromCartItem(i) === key);
            if (existing) snap[key] = { ...existing };
        });

        setConflictSnapshot(snap);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conflictSig]);

    const handleBackToStoreEditing = () => {
        conflicts.forEach((p) => removeFromCart(p.idProducto, p.idProductoVariante));
        navigate("/");
    };

    const handleBackToStoreReview = () => {
        navigate("/");
    };

    const buildNextCartItems = (): CartItem[] => {
        const map = new Map<string, CartItem>();
        cartItems.forEach((i) => map.set(keyFromCartItem(i), { ...i }));

        conflicts.forEach((p) => {
            const key = makeKey(p);
            const r = resolutions[key];
            if (!r) return;

            if (r.action === "remove") {
                map.delete(key);
                return;
            }

            const existing = map.get(key);
            if (existing) {
                existing.quantity = r.quantity;
                existing.stock = p.stockActual;
                map.set(key, existing);
            } else {
                const snap = conflictSnapshot[key];
                if (snap) {
                    map.set(key, { ...snap, quantity: r.quantity, stock: p.stockActual });
                } else {
                    map.set(key, {
                        idProduct: p.idProducto,
                        idProductVariant: p.idProductoVariante,
                        name: p.nombreProducto,
                        price: 0,
                        quantity: r.quantity,
                        image: "",
                        stock: p.stockActual,
                        attributes: [],
                    });
                }
            }
        });

        return Array.from(map.values());
    };

    const applyCartChanges = () => {
        conflicts.forEach((p) => {
            const key = makeKey(p);
            const resolution = resolutions[key];

            if (!resolution || resolution.action === "remove") {
                removeFromCart(p.idProducto, p.idProductoVariante);
                return;
            }

            const exists = cartItems.some((i) => keyFromCartItem(i) === key);

            if (exists) {
                updateQuantity(p.idProducto, p.idProductoVariante, resolution.quantity);
                return;
            }

            const snap = conflictSnapshot[key];
            if (!snap) {
                enqueueSnackbar(
                    `No se pudo re-agregar "${p.nombreProducto}" porque no se encontró su info en el carrito original.`,
                    { variant: "error" },
                );
                return;
            }

            const { quantity: _oldQty, ...rest } = snap;
            addToCart(
                {
                    ...rest,
                    quantity: resolution.quantity,
                    stock: p.stockActual,
                },
                { source: "SYSTEM" },
            );
        });
    };

    const handleSaveChanges = async () => {
        if (!allResolved) return;

        setIsSaving(true);
        try {
            const nextCartItems = buildNextCartItems();

            // 1) aplicar cambios al carrito
            applyCartChanges();

            // 2) recalcular con los productos resultantes
            const products: OrderProduct[] = nextCartItems.map((item) => ({
                [OrderProductFields.ProductId]: item.idProduct,
                [OrderProductFields.ProductVariantId]: item.idProductVariant,
                [OrderProductFields.Quantity]: item.quantity,
            }));

            const calculationData = await recalculateOrder(products);

            if (!calculationData) {
                enqueueSnackbar("No se pudo recalcular la orden. Intente nuevamente.", {
                    variant: "error",
                });
                return;
            }

            const subtotal = calculationData[OrderCalculationViewFields.Subtotal] ?? 0;
            setLastSubtotal(subtotal);

            const ok = subtotal >= precioMinimo;
            setMeetsMinimum(ok);

            setMode("review");

            if (!ok) {
                enqueueSnackbar(
                    "Con estos cambios la orden no alcanza el monto mínimo. Podés seguir ajustando o volver a la tienda para agregar productos.",
                    { variant: "warning" },
                );
                return;
            }

            enqueueSnackbar("Resumen actualizado. Ya podés finalizar la compra.", {
                variant: "success",
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handleFinalize = () => {
        if (!meetsMinimum) return;
        onSubmitOrder();
    };

    if (processStatus.type !== CheckoutProcessStatusType.RequiresStockResolution) {
        return null;
    }

    const reviewDescription = meetsMinimum
        ? "Los cambios se guardaron y el resumen ya refleja los importes correctos. Si está todo OK, finalizó la compra."
        : "Los cambios se guardaron, pero la orden no alcanza el monto mínimo de compra. Podés modificar los cambios o volver a la tienda para agregar productos.";

    // ---- Styles (alineados al drawer) ----
    const radioSx = {
        color: "#d0d0d0",
        "&.Mui-checked": { color: "#010101" },
        "& .MuiSvgIcon-root": { fontSize: 20 },
    };

    const cardSx = {
        border: "1px solid #f0f0f0",
        borderRadius: 0,
        boxShadow: "none",
        p: 2,
    };

    const titleSx = {
        fontFamily: "inherit",
        fontSize: "10px",
        textTransform: "uppercase",
        color: "#010101",
        letterSpacing: "0.04em",
        mb: 1,
    };

    const headingSx = {
        fontFamily: "inherit",
        fontSize: "18px",
        fontWeight: 500,
        color: "#010101",
        lineHeight: 1.3,
    };

    const bodySx = {
        fontFamily: "inherit",
        fontSize: "12px",
        color: "#666",
        lineHeight: 1.5,
    };

    const labelTitleSx = {
        fontFamily: "inherit",
        fontSize: "13px",
        fontWeight: 500,
        color: "#010101",
        lineHeight: 1.4,
    };

    const labelSubSx = {
        fontFamily: "inherit",
        fontSize: "12px",
        color: "#666",
        lineHeight: 1.4,
    };

    const primaryBtnSx = (disabled?: boolean) => ({
        ...BUTTON_STYLES.primary,
        width: "auto",
        ...(disabled
            ? {
                  "&.Mui-disabled": {
                      color: "#bdbdbd",
                      borderBottom: "1px solid #e0e0e0",
                  },
              }
            : {}),
    });

    const secondaryBtnSx = {
        ...BUTTON_STYLES.borderedText,
        height: "40px",
        px: 2,
    };

    const greyBtnSx = {
        ...BUTTON_STYLES.grey,
        height: "40px",
        px: 2,
    };

    return (
        <Box maxWidth="800px" mx="auto" mt={4} px={{ xs: 2, sm: 0 }}>
            <Paper
                sx={{
                    border: "1px solid #f0f0f0",
                    borderRadius: 0,
                    boxShadow: "none",
                    p: { xs: 2, sm: 3 },
                }}
            >
                <Typography sx={titleSx}>
                    {mode === "editing" ? "Stock insuficiente" : "Revisión"}
                </Typography>

                <Typography sx={headingSx} mb={1.5}>
                    {mode === "editing"
                        ? "Algunos productos no tienen stock suficiente"
                        : "Revisá el resumen actualizado"}
                </Typography>

                <Typography sx={bodySx} mb={3}>
                    {mode === "editing"
                        ? "Podés eliminar los productos sin stock o ajustar la cantidad disponible para continuar."
                        : reviewDescription}
                </Typography>

                {mode === "review" && !meetsMinimum && (
                    <Alert
                        severity="warning"
                        sx={{
                            mb: 3,
                            borderRadius: 0,
                            "& .MuiAlert-message": {
                                fontFamily: "inherit",
                                fontSize: "12px",
                                color: "#666",
                            },
                        }}
                    >
                        La compra no supera el monto mínimo.
                        {precioMinimo > 0 && (
                            <>
                                {" "}
                                Mínimo:{" "}
                                <b style={{ color: "#010101" }}>{formatMoney(precioMinimo)}</b>
                            </>
                        )}
                        {lastSubtotal !== null && (
                            <>
                                {" "}
                                Subtotal actual:{" "}
                                <b style={{ color: "#010101" }}>{formatMoney(lastSubtotal)}</b>.
                            </>
                        )}{" "}
                        Modificá los cambios o volvé a la tienda para agregar productos.
                    </Alert>
                )}

                {mode === "editing" && (
                    <Stack spacing={1.5}>
                        {conflicts.map((p) => {
                            const key = makeKey(p);
                            const allowAdjust = canAdjustQuantity(p);

                            return (
                                <Box key={key} sx={cardSx}>
                                    <Typography sx={labelTitleSx} mb={0.5}>
                                        {p.nombreProducto}
                                    </Typography>

                                    <Typography sx={labelSubSx} mb={1.5}>
                                        Stock disponible:{" "}
                                        <b style={{ color: "#010101" }}>{p.stockActual}</b>
                                    </Typography>

                                    <RadioGroup
                                        value={resolutions[key]?.action ?? ""}
                                        onChange={(e) => {
                                            const value = e.target.value;

                                            if (value === "remove") {
                                                setResolutions((prev) => ({
                                                    ...prev,
                                                    [key]: { action: "remove" },
                                                }));
                                            }

                                            if (value === "adjust") {
                                                setResolutions((prev) => ({
                                                    ...prev,
                                                    [key]: {
                                                        action: "adjust",
                                                        quantity: p.stockActual,
                                                    },
                                                }));
                                            }
                                        }}
                                    >
                                        <FormControlLabel
                                            value="remove"
                                            control={<Radio sx={radioSx} />}
                                            label={
                                                <Typography sx={{ ...bodySx, color: "#010101" }}>
                                                    Eliminar del carrito
                                                </Typography>
                                            }
                                            sx={{ m: 0, alignItems: "flex-start" }}
                                        />

                                        {allowAdjust && (
                                            <FormControlLabel
                                                value="adjust"
                                                control={<Radio sx={radioSx} />}
                                                label={
                                                    <Typography
                                                        sx={{ ...bodySx, color: "#010101" }}
                                                    >
                                                        Comprar máximo disponible{" "}
                                                        <span style={{ color: "#666" }}>
                                                            ({p.stockActual})
                                                        </span>
                                                    </Typography>
                                                }
                                                sx={{ m: 0, alignItems: "flex-start" }}
                                            />
                                        )}
                                    </RadioGroup>
                                </Box>
                            );
                        })}
                    </Stack>
                )}

                <Divider sx={{ my: 3 }} />

                {mode === "editing" ? (
                    <Stack
                        direction="row"
                        spacing={2}
                        justifyContent="flex-end"
                        flexWrap="wrap"
                        rowGap={1}
                    >
                        <Button
                            variant="outlined"
                            onClick={handleBackToStoreEditing}
                            disabled={isSaving}
                            sx={greyBtnSx}
                        >
                            Volver a la Tienda
                        </Button>

                        <Button
                            variant="contained"
                            disabled={!allResolved || isSaving}
                            onClick={handleSaveChanges}
                            sx={primaryBtnSx(!allResolved || isSaving)}
                        >
                            {isSaving ? <CircularProgress size={20} /> : "GUARDAR CAMBIOS"}
                        </Button>
                    </Stack>
                ) : (
                    <Stack
                        direction="row"
                        spacing={2}
                        justifyContent="flex-end"
                        flexWrap="wrap"
                        rowGap={1}
                    >
                        <Button
                            variant="text"
                            onClick={() => {
                                setMode("editing");
                                setMeetsMinimum(true);
                                setLastSubtotal(null);
                            }}
                            disabled={isLoadingOrder}
                            sx={greyBtnSx}
                        >
                            Modificar cambios
                        </Button>

                        <Button
                            variant="outlined"
                            onClick={handleBackToStoreReview}
                            disabled={isLoadingOrder}
                            sx={greyBtnSx}
                        >
                            Volver a la Tienda
                        </Button>

                        <Button
                            variant="contained"
                            onClick={handleFinalize}
                            disabled={isLoadingOrder || !meetsMinimum}
                            sx={primaryBtnSx(isLoadingOrder || !meetsMinimum)}
                        >
                            {isLoadingOrder ? <CircularProgress size={20} /> : "FINALIZAR COMPRA"}
                        </Button>
                    </Stack>
                )}
            </Paper>
        </Box>
    );
};
